import { Link } from '@tanstack/react-router';
import { Pencil, Plus } from 'lucide-react';
import {
  DashboardDataTable,
  DashboardDataTableColumnKind,
  buildDashboardDataTableColumns,
  useDashboardDataTableClientPage,
} from '@shigo-ui/dashboard-ui';
import { Button } from '@shigo-ui/ui';

import { dashboardT } from '../../../../../../lib/messages';
import {
  IntegrationAttachedListType,
  type AttachedRewriteList,
  type AttachedSchainList,
} from '../../../integrationRewriteModel';

interface AttachedListRow {
  key: string;
  id: string;
  name: string;
  type: IntegrationAttachedListType;
  mode: string;
}

export function IntegrationAttachedLists(props: {
  rewrites: AttachedRewriteList[];
  schains: AttachedSchainList[];
  onManage: () => void;
  onClear: () => void;
}) {
  const rows: AttachedListRow[] = [
    ...props.rewrites.map((item) => ({
      key: `${IntegrationAttachedListType.Rewrite}:${item.id}`,
      id: item.id,
      name: item.name ?? item.id,
      type: IntegrationAttachedListType.Rewrite,
      mode: '-',
    })),
    ...props.schains.map((item) => ({
      key: `${IntegrationAttachedListType.Schain}:${item.id}`,
      id: item.id,
      name: item.name ?? item.id,
      type: IntegrationAttachedListType.Schain,
      mode: item.type ?? '-',
    })),
  ];
  const columns = buildDashboardDataTableColumns<AttachedListRow>([
    {
      id: 'name',
      header: dashboardT('integrations.rewrite.name'),
      kind: DashboardDataTableColumnKind.Text,
      value: (row) => row.name,
      headerClassName: 'min-w-[260px]',
      className: 'min-w-[260px] font-medium',
    },
    {
      id: 'type',
      header: dashboardT('integrations.rewrite.type'),
      kind: DashboardDataTableColumnKind.Text,
      value: (row) =>
        row.type === IntegrationAttachedListType.Rewrite
          ? dashboardT('lists.tabs.rewrite')
          : dashboardT('lists.tabs.schain'),
      headerClassName: 'w-40',
      className: 'w-40',
    },
    {
      id: 'mode',
      header: dashboardT('integrations.rewrite.mode'),
      kind: DashboardDataTableColumnKind.Text,
      value: (row) => row.mode,
      headerClassName: 'w-40',
      className: 'w-40',
    },
    {
      id: 'actions',
      kind: DashboardDataTableColumnKind.Actions,
      cell: (row) => (
        <Button
          asChild
          type="button"
          size="icon"
          variant="ghost"
          aria-label={dashboardT('integrations.rewrite.editList', {
            name: row.name,
          })}
        >
          <Link
            to={
              row.type === IntegrationAttachedListType.Rewrite
                ? '/supply/lists/rewrite/edit/$listId'
                : '/supply/lists/schain/edit/$listId'
            }
            params={{ listId: row.id }}
          >
            <Pencil className="h-4 w-4" aria-hidden="true" />
          </Link>
        </Button>
      ),
    },
  ]);

  // Long lists render one page of rows, not every row (keeps tab switches fast).
  const tablePage = useDashboardDataTableClientPage(rows);
  return (
    <DashboardDataTable
      title={dashboardT('integrations.rewrite.attachedLists')}
      tableLabel={dashboardT('integrations.rewrite.attachedLists')}
      columns={columns}
      items={tablePage.items}
      pagination={tablePage.pagination}
      getRowKey={(row) => row.key}
      emptyMessage={dashboardT('integrations.rewrite.noListsDescription')}
      minWidthClassName="min-w-[680px]"
      toolbar={
        <>
          <Button type="button" variant="outline" onClick={props.onManage}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            {dashboardT('integrations.rewrite.manageLists')}
          </Button>
          {rows.length ? (
            <Button type="button" variant="ghost" onClick={props.onClear}>
              {dashboardT('integrations.rewrite.clearLists')}
            </Button>
          ) : null}
        </>
      }
    />
  );
}
